import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

interface Step {
  number: number;
  title: string;
  description: string;
  image?: string;
  icon?: React.ReactNode;
}

interface StepProcessProps {
  title: string;
  subtitle?: string;
  steps: Step[];
  ctaText?: string;
  ctaLink?: string;
  layout?: 'grid' | 'horizontal' | 'vertical';
  background?: 'light' | 'dark' | 'gradient';
}

const getBackgroundColor = (bg?: 'light' | 'dark' | 'gradient') => {
  switch (bg) {
    case 'dark':
      return '#2d3748';
    case 'gradient':
      return 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)';
    case 'light':
    default:
      return '#f8f9fa';
  }
};

const getTextColor = (bg?: 'light' | 'dark' | 'gradient') => {
  return bg === 'dark' ? '#ffffff' : '#212529';
};

const StepProcessContainer = styled.section<{ background?: 'light' | 'dark' | 'gradient' }>`
  padding: 5rem 1rem;
  background: ${props => getBackgroundColor(props.background)};
  color: ${props => getTextColor(props.background)};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
  }
`;

const StepProcessHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
`;

const StepProcessTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const StepProcessSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--gray);
  margin-bottom: 2rem;
`;

const StepsGridContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  
  @media (min-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StepsHorizontalContainer = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: center;
  gap: 2rem;
  
  @media (min-width: 992px) {
    flex-wrap: nowrap;
  }
`;

const StepsVerticalContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 40px;
    width: 4px;
    background: linear-gradient(to bottom, #007bff 0%, #0056b3 100%);
    
    @media (max-width: 768px) {
      left: 30px;
    }
  }
`;

const StepItem = styled.div`
  background-color: var(--white);
  border-radius: 1rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  }
`;

const StepHorizontalItem = styled(StepItem)`
  flex: 1;
  min-width: 250px;
  max-width: 350px;
`;

const StepVerticalItem = styled.div`
  position: relative;
  padding-left: 80px;
  padding-bottom: 3rem;
  
  @media (max-width: 768px) {
    padding-left: 60px;
  }
  
  &:last-child {
    padding-bottom: 0;
  }
`;

const StepNumber = styled.div`
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: var(--white);
  font-size: 1.5rem;
  font-weight: bold;
  padding: 0.5rem 1.5rem;
  display: inline-block;
  border-radius: 0 0 1rem 0;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const StepVerticalNumber = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: var(--white);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
  }
`;

const StepImageContainer = styled.div`
  position: relative;
  padding-top: 60%;
  background-color: #f8f9fa;
  overflow: hidden;
`;

const StepImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${StepItem}:hover & {
    transform: scale(1.05);
  }
`;

const StepContent = styled.div`
  padding: 2rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StepVerticalContent = styled.div`
  background-color: var(--white);
  border-radius: 1rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  padding: 2rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  }
`;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.75rem;
    color: var(--blue);
  }
`;

const StepDescription = styled.p`
  font-size: 1rem;
  color: var(--gray);
  line-height: 1.6;
`;

const CtaContainer = styled.div`
  text-align: center;
  margin-top: 3rem;
`;

const CtaButton = styled(Link)`
  display: inline-block;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: var(--white);
  font-size: 1.1rem;
  font-weight: 500;
  padding: 1rem 2rem;
  border-radius: 0.5rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #0056b3;
    color: var(--white);
    transform: translateY(-3px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.15);
    text-decoration: none;
  }
`;

const StepProcess: React.FC<StepProcessProps> = ({
  title,
  subtitle,
  steps,
  ctaText = "Empezar ahora",
  ctaLink = "/contacto",
  layout = 'grid',
  background = 'light'
}) => {
  const renderSteps = () => {
    switch (layout) {
      case 'horizontal':
        return (
          <StepsHorizontalContainer>
            {steps.map((step) => (
              <StepHorizontalItem key={step.number}>
                <StepImageContainer>
                  <StepNumber>{step.number}</StepNumber>
                  {step.image && <StepImage src={step.image} alt={step.title} />}
                </StepImageContainer>
                <StepContent>
                  <StepTitle>
                    {step.icon && step.icon}
                    {step.title}
                  </StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </StepContent>
              </StepHorizontalItem>
            ))}
          </StepsHorizontalContainer>
        );
      case 'vertical':
        return (
          <StepsVerticalContainer>
            {steps.map((step) => (
              <StepVerticalItem key={step.number}>
                <StepVerticalNumber>{step.number}</StepVerticalNumber>
                <StepVerticalContent>
                  <StepTitle>
                    {step.icon && step.icon}
                    {step.title}
                  </StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </StepVerticalContent>
              </StepVerticalItem>
            ))}
          </StepsVerticalContainer>
        );
      case 'grid':
      default:
        return (
          <StepsGridContainer>
            {steps.map((step) => (
              <StepItem key={step.number}>
                <StepImageContainer>
                  <StepNumber>{step.number}</StepNumber>
                  {step.image && <StepImage src={step.image} alt={step.title} />}
                </StepImageContainer>
                <StepContent>
                  <StepTitle>
                    {step.icon && step.icon}
                    {step.title}
                  </StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </StepContent>
              </StepItem>
            ))}
          </StepsGridContainer>
        );
    }
  };

  return (
    <StepProcessContainer background={background}>
      <StepProcessHeader>
        <StepProcessTitle>{title}</StepProcessTitle>
        {subtitle && <StepProcessSubtitle>{subtitle}</StepProcessSubtitle>}
      </StepProcessHeader>
      
      {renderSteps()}
      
      {ctaText && (
        <CtaContainer>
          <CtaButton to={ctaLink}>{ctaText}</CtaButton>
        </CtaContainer>
      )}
    </StepProcessContainer>
  );
};

export default StepProcess; 